import { useMemo } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { MoneyOutput } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { CurrencyFormatOptions } from '@/views/TicketCheckoutViews/hooks/usePriceFormatter'

interface PriceOptions extends Omit<CurrencyFormatOptions, 'currency'> {
  negative?: boolean
}

export const usePrice = (amount: Maybe<MoneyOutput>, options?: PriceOptions): string => {
  const { locale } = useLocale()

  return useMemo(() => {
    if (!amount) return ''

    return getPrice(amount, locale, options)
  }, [amount, locale, options])
}

export const getPrice = (amount: MoneyOutput, locale: string, options?: PriceOptions): string => {
  const { negative, ...opts } = options ?? {}
  const priceFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: amount.currencyCode,
    maximumFractionDigits: opts?.maximumFractionDigits,
    minimumFractionDigits: opts?.minimumFractionDigits,
  })

  if (!isFinite(amount?.currencyAmount)) return ''

  const multiplier = negative ? -1 : 1

  return priceFormatter.format(multiplier * amount.currencyAmount)
}
